import React, { useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import "./privacy.css";

export default function TermsOfService() {
  const navigate = useNavigate();
  const location = useLocation();
  // In the app this page carries the fixed header + tab bar (Profile tab lit),
  // so it owns its scroll — see .legal-scroll in privacy.css. Same test the
  // tab bar itself uses; on logged-out web the wrapper is class-less and the
  // document scrolls past the footer exactly as before.
  const { user, isAuthenticated } = useContext(AuthContext);
  const inApp = isAuthenticated && !!user;
  // Back to wherever they came from within the app. If they landed here directly
  // (external/deep link — router key is "default"), go to the home page instead
  // of navigate(-1), which would eject them OUT of the app. (These are public
  // pages, also reachable from the footer / SEO / App Store.)
  const goBack = () => {
    if (location.key !== "default") navigate(-1);
    else navigate("/");
  };
  useEffect(() => {
    // Reset scroll so you land at the top (with the back arrow) — navigation
    // otherwise keeps the previous page's scroll (same fix as LeaderboardPage).
    window.scrollTo(0, 0);
    document.title = "Terms of Service — Request App";
  }, []);

  return (
    <div className={inApp ? "legal-scroll" : undefined}>
    <main className="rqpv-wrap" role="main">
      <div className="rqpv-card">
        <button type="button" className="rqpv-back" onClick={goBack} aria-label="Back" title="Back">
          <FaArrowLeft size={16} />
        </button>
        <header className="rqpv-header">
          <div className="rqpv-title">Terms of Service</div>
          <div className="rqpv-meta">
            <span>Effective Date: November 2, 2026</span>
            <span>Company: REQUESTA LLC (Wyoming, USA)</span>
            <span>Platform: Web (requesta.app)</span>
            <span>Contact: <a className="rqpv-link" href="mailto:support@requesta.app">support@requesta.app</a></span>
          </div>
        </header>


        <section id="tos-acceptance" className="rqpv-section">
          <div className="rqpv-h2">1. Acceptance of Terms</div>
          <div className="rqpv-text">
            By accessing or using Request App ("the App"), you agree to these Terms of Service ("Terms").
            If you do not agree, do not use the App.
          </div>
        </section>

        <section id="tos-eligibility" className="rqpv-section">
          <div className="rqpv-h2">2. Eligibility</div>
          <div className="rqpv-text">
            You must be at least 13 years old to use the App. If you are under 18, you represent
            that you have the consent of a parent or guardian.
          </div>
        </section>

        <section id="tos-accounts" className="rqpv-section">
          <div className="rqpv-h2">3. User Accounts</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">You must provide accurate registration information.</li>
            <li className="rqpv-li">You are responsible for maintaining the confidentiality of your login credentials.</li>
            <li className="rqpv-li">We may suspend or terminate accounts at our sole discretion.</li>
          </ul>
        </section>

        <section id="tos-payments" className="rqpv-section">
          <div className="rqpv-h2">4. Payments</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">Payments are processed securely through Stripe, Apple Pay, and Link.</li>
            <li className="rqpv-li">REQUESTA LLC does not store payment card details.</li>
            <li className="rqpv-li">Refunds and chargebacks are handled in accordance with Stripe and Apple Pay policies.</li>
            <li className="rqpv-li"><strong>Payment collection:</strong> Each performer (DJ or other talent) appoints REQUESTA LLC as their limited payment collection agent, solely to accept fan payments for the performer&apos;s services on Request App. A fan&apos;s payment to REQUESTA LLC or its payment processor is treated as payment to the performer and satisfies the fan&apos;s obligation for that request. REQUESTA LLC is not a bank, and balances do not earn interest.</li>
            <li className="rqpv-li"><strong>How performers are paid:</strong> In countries we pay through Stripe, each performer has their own Stripe account, governed by Stripe&apos;s terms; Stripe transfers the performer&apos;s share to that account, and REQUESTA LLC keeps only its fees. In countries we pay through MassPay, the fan&apos;s payment settles to REQUESTA LLC and the performer&apos;s share is recorded in their Request App balance; the performer authorizes REQUESTA LLC to instruct MassPay to send that balance to their MassPay wallet when they cash out.</li>
            <li className="rqpv-li"><strong>Cash-outs:</strong> Earnings can be cashed out after the fan has paid. A holding period may apply to new earnings, and payouts may be delayed where needed to prevent fraud or comply with the law.</li>
            <li className="rqpv-li"><strong>MassPay wallet:</strong> A payout is complete once it is credited to the performer&apos;s MassPay wallet. Withdrawals from the wallet are provided by MassPay, and MassPay&apos;s withdrawal and currency-conversion fees are paid by the performer. If a payout cannot be completed, the amount stays in the performer&apos;s Request App balance.</li>
            <li className="rqpv-li"><strong>Performer chargeback liability:</strong> If a fan&apos;s payment is refunded, reversed or disputed, REQUESTA LLC may hold or recover the performer&apos;s share of that payment, and any related dispute fee, from the performer&apos;s balance or future payouts, or by reversing the related transfer to the performer&apos;s Stripe account.</li>
            <li className="rqpv-li"><strong>Dispute defense:</strong> Request App provides tools to help performers defend disputes, including 3D Secure authentication at checkout, evidence preparation, and the ability to upload track history as proof of fulfillment. Performers are responsible for responding promptly to dispute notifications and submitting any requested evidence within the timeframe communicated by Stripe.</li>
            <li className="rqpv-li"><strong>Not-played claims:</strong> If a fan tells us a paid request wasn&apos;t played (within 7 days of it being marked played), the performer is notified and has 3 days to upload their track history or refund. If the song is in the track history, the request stays paid and that fan can&apos;t claim again. If nothing naming the song comes in within 3 days, the fan is refunded and the amount comes off the performer&apos;s earnings, the same as a refund they make themselves.</li>
            <li className="rqpv-li"><strong>Launch offer:</strong> Talent who sign up with a Request App launch-offer code while spots last (the first 1,500) pay 0% platform commission on everything they earn for 3 months from sign-up; after that, the normal tier rates apply. Card processing fees paid by fans and the optional instant-payout fee are not commission and still apply. We may stop offering the launch offer to new sign-ups at any time, but we will never end it early for talent who already have it.</li>
          </ul>
        </section>

        <section id="tos-content" className="rqpv-section">
          <div className="rqpv-h2">5. User Content</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">Users retain ownership of their content (such as song requests and messages).</li>
            <li className="rqpv-li">By submitting content, you grant REQUESTA LLC a worldwide, non-exclusive license to display and transmit that content solely to operate the App.</li>
            <li className="rqpv-li"><strong>Zero tolerance:</strong> There is no tolerance for objectionable content or abusive users. Content that other users can see, such as posts on the Feedback board, is filtered for objectionable words, and any user can report it; reporting a post also hides that person&apos;s posts from you. We review reports, remove objectionable content, and suspend accounts that post it.</li>
          </ul>
        </section>

        <section id="tos-prohibited" className="rqpv-section">
          <div className="rqpv-h2">6. Prohibited Uses</div>
          <div className="rqpv-text">You may not:</div>
          <ul className="rqpv-list">
            <li className="rqpv-li">Use the App for unlawful, fraudulent, or abusive purposes.</li>
            <li className="rqpv-li">Interfere with or disrupt the App's functionality.</li>
            <li className="rqpv-li">Infringe upon the intellectual property or rights of others.</li>
          </ul>
        </section>

        <section id="tos-liability" className="rqpv-section">
          <div className="rqpv-h2">7. Limitation of Liability</div>
          <div className="rqpv-text">
            The App is provided "as is." REQUESTA LLC is not liable for missed requests, technical
            issues, outages, lost profits, or damages arising from use of the App.
          </div>
        </section>

        <section id="tos-termination" className="rqpv-section">
          <div className="rqpv-h2">8. Termination</div>
          <div className="rqpv-text">
            We may suspend or terminate access to the App at any time, without notice, for violations of these Terms.
          </div>
        </section>

        <section id="tos-law" className="rqpv-section">
          <div className="rqpv-h2">9. Governing Law</div>
          <div className="rqpv-text">
            These Terms are governed by the laws of Wyoming, USA. Any disputes will be resolved in Wyoming courts.
          </div>
        </section>

        <section id="tos-changes" className="rqpv-section">
          <div className="rqpv-h2">10. Changes</div>
          <div className="rqpv-text">
            We may update these Terms at any time. Continued use of the App constitutes acceptance of the updated Terms.
          </div>
        </section>

        <section id="tos-sms" className="rqpv-section">
          <div className="rqpv-h2">11. SMS Terms &amp; Conditions</div>
          <ul className="rqpv-list">
            <li className="rqpv-li"><strong>Program Name:</strong> Request App SMS Alerts</li>
            <li className="rqpv-li"><strong>Program Description:</strong> Request App sends transactional SMS alerts to performers related to pending requests, payments, account activity, and other service-related notifications.</li>
            <li className="rqpv-li"><strong>Message Frequency:</strong> Message frequency varies based on account activity and user actions.</li>
            <li className="rqpv-li"><strong>Opt-Out:</strong> Reply STOP to cancel SMS messages at any time.</li>
            <li className="rqpv-li"><strong>Help:</strong> Reply HELP for help. You may also contact support at <a className="rqpv-link" href="mailto:support@requesta.app">support@requesta.app</a>.</li>
            <li className="rqpv-li"><strong>Consent:</strong> By providing your phone number and opting in, you agree to receive SMS messages from Request App related to your account. SMS consent is not a condition of purchase or use of the service.</li>
            <li className="rqpv-li"><strong>Message &amp; Data Rates:</strong> Standard message and data rates may apply.</li>
            <li className="rqpv-li"><strong>Privacy:</strong> See our <a className="rqpv-link" href="/privacy">Privacy Policy</a>. Your phone number and SMS consent are never shared with third parties for marketing purposes.</li>
            <li className="rqpv-li"><strong>Supported Carriers:</strong> Available on all major US carriers including AT&amp;T, Verizon, T-Mobile, and others.</li>
          </ul>
        </section>

        <section id="tos-playlists" className="rqpv-section">
          <div className="rqpv-h2">12. Playlists</div>
          <ul className="rqpv-list">
            <li className="rqpv-li"><strong>What you buy:</strong> A playlist on Request App is a performer&apos;s own list of songs. When you buy one, we send you a page with that list and a way to add it to your own Spotify or Apple Music account.</li>
            <li className="rqpv-li"><strong>What isn&apos;t included:</strong> Request App does not sell, host, stream or license music. To listen, you need your own account with Spotify or Apple Music, and that service&apos;s terms apply. Spotify and Apple are not connected to Request App and do not endorse it.</li>
            <li className="rqpv-li"><strong>When you pay:</strong> A saved playlist is charged when you buy it and delivered right away. A night&apos;s set is charged only when the performer delivers it; if they don&apos;t, the hold on your card is released and you pay nothing.</li>
            <li className="rqpv-li"><strong>Performers:</strong> Each performer sells their own playlists, and section 4 applies to those sales. By listing a playlist, a performer confirms the list is their own and that nobody paid them to include a song.</li>
          </ul>
        </section>

        <div className="rqpv-updated">Effective Date: November 2, 2026</div>
      </div>
    </main>
    </div>
  );
}
